import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  FileText,
  Globe,
  Sparkles,
  Send,
  DollarSign,
  TrendingUp,
  AlertCircle,
  CheckCircle,
  Clock,
  Loader2,
  BarChart3,
} from 'lucide-react'
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
} from 'recharts'

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { analyticsApi } from '@/services/api'
import type { AnalyticsResponse, RecentArticle } from '@/types'

// v2 pipeline status colors
const statusColors: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-700',
  pending: 'bg-yellow-100 text-yellow-700',
  // v2 pipeline statuses
  planning: 'bg-blue-100 text-blue-700',
  writing: 'bg-blue-100 text-blue-700',
  optimizing: 'bg-purple-100 text-purple-700',
  finalizing: 'bg-orange-100 text-orange-700',
  generating_image: 'bg-cyan-100 text-cyan-700',
  ready: 'bg-green-100 text-green-700',
  published: 'bg-purple-100 text-purple-700',
  failed: 'bg-red-100 text-red-700',
  // Legacy v1 statuses
  researching: 'bg-blue-100 text-blue-700',
  editing: 'bg-orange-100 text-orange-700',
  humanizing: 'bg-pink-100 text-pink-700',
}

function formatCost(cost: number): string {
  if (cost === 0) return '$0.00'
  if (cost < 0.01) return `$${cost.toFixed(4)}`
  return `$${cost.toFixed(2)}`
}

function formatScore(score: number | null | undefined): string {
  if (score === null || score === undefined) return '-'
  return `${Math.round(score)}`
}

function StatCard({
  title,
  value,
  description,
  icon: Icon,
  trend,
}: {
  title: string
  value: string | number
  description: string
  icon: typeof FileText
  trend?: 'up' | 'down' | 'neutral'
}) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
        <Icon className="h-4 w-4 text-muted-foreground" />
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">{value}</div>
        <p className="text-xs text-muted-foreground flex items-center gap-1">
          {trend === 'up' && <TrendingUp className="h-3 w-3 text-green-500" />}
          {trend === 'down' && <TrendingUp className="h-3 w-3 text-red-500 rotate-180" />}
          {description}
        </p>
      </CardContent>
    </Card>
  )
}

function RecentArticleRow({ article, t }: { article: RecentArticle; t: (key: string) => string }) {
  const navigate = useNavigate()

  return (
    <div
      className="flex items-center justify-between p-2 rounded-md hover:bg-secondary/50 cursor-pointer transition-colors"
      onClick={() => navigate(`/articles`)}
    >
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">{article.title || t('dashboard.untitled')}</p>
        <p className="text-xs text-muted-foreground">
          {article.site_name || t('dashboard.noSite')} &middot; {article.word_count} {t('dashboard.words')}
        </p>
      </div>
      <div className="flex items-center gap-2 ms-2">
        {article.seo_score !== null && (
          <span className="text-xs text-muted-foreground">SEO: {article.seo_score}</span>
        )}
        <span className={`px-2 py-0.5 text-xs rounded-full ${statusColors[article.status] || statusColors.draft}`}>
          {t(`dashboard.status.${article.status}`)}
        </span>
      </div>
    </div>
  )
}

export function DashboardPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const { data, isLoading, error } = useQuery<AnalyticsResponse>({
    queryKey: ['analytics'],
    queryFn: () => analyticsApi.getAnalytics(30),
    refetchInterval: 30000, // Refresh every 30 seconds
  })

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-center">
        <AlertCircle className="h-8 w-8 text-destructive mb-2" />
        <p className="text-destructive">{t('errors.failedToLoadAnalytics')}</p>
        <p className="text-sm text-muted-foreground">{(error as Error).message}</p>
      </div>
    )
  }

  const overview = data?.overview
  const recentArticles = data?.recent_articles || []
  const costTrends = data?.cost_trends || []

  // Aggregate article trends by date for the chart
  const articleTrendsByDate = (data?.article_trends || []).reduce(
    (acc, trend) => {
      const existing = acc.find((a) => a.date === trend.date)
      if (existing) {
        existing.count += trend.count
      } else {
        acc.push({ date: trend.date, count: trend.count })
      }
      return acc
    },
    [] as { date: string; count: number }[]
  )

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">{t('dashboard.title')}</h1>
        <p className="text-muted-foreground">{t('dashboard.description')}</p>
      </div>

      {/* Stats Grid */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title={t('dashboard.totalArticles')}
          value={overview?.total_articles || 0}
          description={t('dashboard.readyToPublish', { count: overview?.ready_articles || 0 })}
          icon={FileText}
        />
        <StatCard
          title={t('dashboard.wordPressSites')}
          value={overview?.total_sites || 0}
          description={t('dashboard.active', { count: overview?.active_sites || 0 })}
          icon={Globe}
        />
        <StatCard
          title={t('dashboard.inProgress')}
          value={overview?.in_progress_articles || 0}
          description={t('dashboard.failed', { count: overview?.failed_articles || 0 })}
          icon={overview?.in_progress_articles ? Loader2 : Sparkles}
        />
        <StatCard
          title={t('dashboard.totalCost')}
          value={formatCost(overview?.total_cost || 0)}
          description={`${t('dashboard.avgSeoScore')}: ${formatScore(overview?.avg_seo_score)}`}
          icon={DollarSign}
        />
      </div>

      {/* Secondary Stats */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">{t('dashboard.avgSeoScoreTitle')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-blue-600">
              {formatScore(overview?.avg_seo_score)}
              <span className="text-lg text-muted-foreground">/100</span>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">{t('dashboard.avgGeoScoreTitle')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-purple-600">
              {formatScore(overview?.avg_geo_score)}
              <span className="text-lg text-muted-foreground">/100</span>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">{t('dashboard.avgHumanScoreTitle')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-green-600">
              {formatScore(overview?.avg_human_score)}
              <span className="text-lg text-muted-foreground">%</span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts and Recent Articles */}
      <div className="grid gap-4 md:grid-cols-2">
        {/* Article Trends Chart */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="h-4 w-4" />
              {t('dashboard.articlesGenerated')}
            </CardTitle>
            <CardDescription>{t('dashboard.last30Days')}</CardDescription>
          </CardHeader>
          <CardContent>
            {articleTrendsByDate.length > 0 ? (
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={articleTrendsByDate}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 10 }}
                    tickFormatter={(value) => new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  />
                  <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                  <Tooltip
                    labelFormatter={(value) => new Date(value as string).toLocaleDateString()}
                    formatter={(value) => [value as number, t('dashboard.articles')]}
                  />
                  <Bar dataKey="count" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex flex-col items-center justify-center h-[200px] text-muted-foreground">
                <BarChart3 className="h-8 w-8 mb-2 opacity-50" />
                <p className="text-sm">{t('dashboard.noArticleData')}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Cost Trends Chart */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="h-4 w-4" />
              {t('dashboard.generationCosts')}
            </CardTitle>
            <CardDescription>{t('dashboard.dailySpending')}</CardDescription>
          </CardHeader>
          <CardContent>
            {costTrends.length > 0 ? (
              <ResponsiveContainer width="100%" height={200}>
                <AreaChart data={costTrends}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 10 }}
                    tickFormatter={(value) => new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  />
                  <YAxis
                    tick={{ fontSize: 10 }}
                    tickFormatter={(value) => `$${value.toFixed(2)}`}
                  />
                  <Tooltip
                    labelFormatter={(value) => new Date(value as string).toLocaleDateString()}
                    formatter={(value) => [`$${(value as number).toFixed(4)}`, t('dashboard.cost')]}
                  />
                  <Area
                    type="monotone"
                    dataKey="cost"
                    stroke="hsl(var(--primary))"
                    fill="hsl(var(--primary) / 0.2)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex flex-col items-center justify-center h-[200px] text-muted-foreground">
                <DollarSign className="h-8 w-8 mb-2 opacity-50" />
                <p className="text-sm">{t('dashboard.noCostData')}</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Bottom Section */}
      <div className="grid gap-4 md:grid-cols-2">
        {/* Recent Articles */}
        <Card>
          <CardHeader>
            <CardTitle>{t('dashboard.recentArticles')}</CardTitle>
            <CardDescription>{t('dashboard.recentArticlesDesc')}</CardDescription>
          </CardHeader>
          <CardContent>
            {recentArticles.length > 0 ? (
              <div className="space-y-1">
                {recentArticles.map((article) => (
                  <RecentArticleRow key={article.id} article={article} t={t} />
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <FileText className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">{t('dashboard.noArticlesYet')}</p>
              </div>
            )}
            <Button variant="outline" className="w-full mt-4" onClick={() => navigate('/articles')}>
              {t('dashboard.viewAllArticles')}
            </Button>
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <Card>
          <CardHeader>
            <CardTitle>{t('dashboard.quickActions')}</CardTitle>
            <CardDescription>{t('dashboard.quickActionsDesc')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <Button
              className="w-full justify-start"
              variant="outline"
              onClick={() => navigate('/articles')}
            >
              <Sparkles className="me-2 h-4 w-4" />
              {t('dashboard.generateNewArticle')}
            </Button>
            <Button
              className="w-full justify-start"
              variant="outline"
              onClick={() => navigate('/sites')}
            >
              <Globe className="me-2 h-4 w-4" />
              {t('dashboard.manageWordPressSites')}
            </Button>
            {(overview?.ready_articles || 0) > 0 && (
              <Button
                className="w-full justify-start"
                variant="outline"
                onClick={() => navigate('/articles')}
              >
                <Send className="me-2 h-4 w-4" />
                {t('dashboard.publishReadyArticles', { count: overview?.ready_articles })}
              </Button>
            )}
            {(overview?.failed_articles || 0) > 0 && (
              <Button
                className="w-full justify-start text-destructive"
                variant="outline"
                onClick={() => navigate('/articles')}
              >
                <AlertCircle className="me-2 h-4 w-4" />
                {t('dashboard.reviewFailedArticles', { count: overview?.failed_articles })}
              </Button>
            )}
            <Button
              className="w-full justify-start"
              variant="outline"
              onClick={() => navigate('/settings')}
            >
              <BarChart3 className="me-2 h-4 w-4" />
              {t('dashboard.configureAgentSettings')}
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Status Summary */}
      <Card>
        <CardHeader>
          <CardTitle>{t('dashboard.statusSummary')}</CardTitle>
          <CardDescription>{t('dashboard.statusSummaryDesc')}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="text-center p-3 bg-secondary/50 rounded-lg">
              <Clock className="h-5 w-5 mx-auto mb-1 text-yellow-600" />
              <div className="text-2xl font-bold">{overview?.in_progress_articles || 0}</div>
              <div className="text-xs text-muted-foreground">{t('dashboard.status.inProgress')}</div>
            </div>
            <div className="text-center p-3 bg-secondary/50 rounded-lg">
              <CheckCircle className="h-5 w-5 mx-auto mb-1 text-green-600" />
              <div className="text-2xl font-bold">{overview?.ready_articles || 0}</div>
              <div className="text-xs text-muted-foreground">{t('dashboard.status.ready')}</div>
            </div>
            <div className="text-center p-3 bg-secondary/50 rounded-lg">
              <Send className="h-5 w-5 mx-auto mb-1 text-purple-600" />
              <div className="text-2xl font-bold">{overview?.published_articles || 0}</div>
              <div className="text-xs text-muted-foreground">{t('dashboard.status.published')}</div>
            </div>
            <div className="text-center p-3 bg-secondary/50 rounded-lg">
              <AlertCircle className="h-5 w-5 mx-auto mb-1 text-red-600" />
              <div className="text-2xl font-bold">{overview?.failed_articles || 0}</div>
              <div className="text-xs text-muted-foreground">{t('dashboard.status.failed')}</div>
            </div>
            <div className="text-center p-3 bg-secondary/50 rounded-lg">
              <FileText className="h-5 w-5 mx-auto mb-1 text-blue-600" />
              <div className="text-2xl font-bold">{overview?.avg_word_count || 0}</div>
              <div className="text-xs text-muted-foreground">{t('dashboard.avgWords')}</div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
